import React, { useCallback, useEffect, useMemo } from 'react';
import { CheckboxConfig } from '@kemu-io/kemu-core/types';
import variablesManager, { getControlScopedVarName } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import classNames from 'classnames';
import tinycolor from 'tinycolor2';
import { DataType } from '@kemu-io/hs-types';
import FieldLabel from '../FieldLabel/FieldLabel';
import styles from './CheckboxField.module.css';
import FormGroup from '@components/form-control/formGroup/formGroup';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import useForceUpdate from '@hooks/useForceReload';


type Props = {
  label: string;
  variableName: string;
  recipeId: string;
  thingRecipeId: string;
  widgetId: string;
  color?: string;
  disabled?: boolean;
  isBrightColor?: boolean;
  isSingleElement: boolean;
  config: CheckboxConfig;
  settingsIndex: number;
  onUpdateConfig: (settingsIndex: number, newConfig: CheckboxConfig) => void;
}

const CheckboxField = (props: Props) => {
  const { variableName, widgetId, disabled, label, isSingleElement, color, isBrightColor, config, settingsIndex, onUpdateConfig } = props;
  const scopedVarName = getControlScopedVarName(variableName);
  const forceUpdate = useForceUpdate();

  const variableValue = variablesManager.getVariableValue({
    variableName: scopedVarName,
    readerWidgetId: widgetId,
  });

  const selected = variableValue?.value as boolean ??  config.customValue ?? config.defaultValue ?? false;

  const checkColor = useMemo(() => {
    if (!color) { return undefined; }
    if (isBrightColor) {
      return tinycolor(color).complement().toString();
    }

    return tinycolor(color).lighten(10).toString();
  }, [color, isBrightColor]);

  const handleSwitchChange = useCallback((checked: boolean) => {
    variablesManager.setVariableValue({
      variableName: scopedVarName,
      setByWidgetId: widgetId,
      value: checked,
      type: DataType.Boolean,
    });

    onUpdateConfig(settingsIndex, {
      ...config,
      customValue: checked,
    } as CheckboxConfig);
  }, [scopedVarName, widgetId, onUpdateConfig, settingsIndex, config]);

  // Subscribe to variable changes to trigger re-render
  useEffect(() => {
    const unsubscribe = variablesManager.onVariableValueChange({
      variableName: scopedVarName,
      type: DataType.Anything,
      widgetId: widgetId,
      onValueChange: forceUpdate,
    });

    return unsubscribe;
  }, [scopedVarName, widgetId, forceUpdate]);

  return (
    <FormGroup noMarginBottom className={classNames(styles.Group, {
      [styles.SingleElement]: isSingleElement && !label,
      [styles.Pb5]: isSingleElement && label
    })}>
      <FieldLabel color={props.color} label={label} />
      <KemuSwitch
        disabled={disabled}
        checked={selected}
        onChange={handleSwitchChange}
        size="default"
        style={{
          ...(selected? { backgroundColor: checkColor }:{})
        }}
      />
    </FormGroup>
  );
};

export default CheckboxField;
